import React, { useEffect, useState } from 'react';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel,
  FormHelperText, Grid2 as Grid, InputLabel, MenuItem, Select, Stack, Switch, TextField,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';
import { axiosService } from 'shared/helpers/axios';
import { apiErrorMessage } from 'shared/helpers/apiError';
import { useUserContext } from 'shared/contexts/UserContext';
import { useLocale } from 'shared/contexts/LocaleContext';
import { useMyLabs } from 'shared/hooks/useMyLabs';
import { useUserConsortia } from 'shared/hooks/useUserConsortia';
import { EVENT_TYPES, VISIBILITY } from './eventMeta';

const DATE_FORMAT_MAP = { dmy: 'DD/MM/YYYY HH:mm', mdy: 'MM/DD/YYYY HH:mm', ymd: 'YYYY-MM-DD HH:mm' };
const toISO = (dj) => (dj && dj.isValid() ? dj.toISOString() : null);

const emptyForm = (defaults) => ({
  title: '', description: '', event_type: 'conference', start_date: null, end_date: null,
  location: '', is_online: false, online_url: '', website: '', organizer: '', category_ids: [],
  visibility: 'public', lab: '', consortium: '', ...defaults,
});

const fromEvent = (e) => ({
  title: e.title || '', description: e.description || '', event_type: e.event_type || 'conference',
  start_date: e.start_date || null, end_date: e.end_date || null, location: e.location || '',
  is_online: Boolean(e.is_online), online_url: e.online_url || '', website: e.website || '',
  organizer: e.organizer || '', category_ids: (e.categories || []).map((c) => c.id),
  visibility: e.visibility || 'public', lab: e.lab || '', consortium: e.consortium || '',
});

/** Create an event, or edit one (EVENT). Anyone can make one for a lab or consortium they belong to, or for
 *  the people they invite; one for everyone on Otable needs a verified affiliation. ZINDEX lifts the dialog
 *  and its pickers above full-screen overlays such as the ProposalWriter. */
export default function EventFormDialog({ open, onClose, event, onSaved, defaults, zIndex }) {
  const { user } = useUserContext();
  const { datePref } = useLocale();
  const labs = useMyLabs(open);
  const { consortia } = useUserConsortia();
  const verified = (user?.org_memberships || []).some((m) => m.is_verified) || user?.is_staff;
  const editing = Boolean(event?.id);

  const [form, setForm] = useState(emptyForm(defaults));
  const [categories, setCategories] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setForm(editing ? fromEvent(event) : emptyForm({ visibility: verified ? 'public' : 'private', ...defaults }));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open || categories.length) return;
    // research areas only: no countries, Horizon clusters or programme labels
    axiosService.get('/events/research-areas/').then(({ data }) => setCategories(data || [])).catch(() => {});
  }, [open, categories.length]);

  const set = (name) => (e) => setForm((f) => ({ ...f, [name]: e.target.value }));
  const popper = zIndex ? { popper: { sx: { zIndex: zIndex + 1 } } } : {};
  const menu = zIndex ? { MenuProps: { sx: { zIndex: zIndex + 1 } } } : {};

  const save = async () => {
    if (!form.title.trim() || !form.start_date) { setError('A title and a start are required.'); return; }
    setBusy(true);
    setError(null);
    const body = {
      ...form,
      lab: form.visibility === 'lab' ? form.lab || null : null,
      consortium: form.visibility === 'consortium' ? form.consortium || null : null,
      website: form.website || null,
      online_url: form.is_online ? form.online_url : '',
    };
    try {
      const { data } = editing
        ? await axiosService.patch(`/events/${event.id}/`, body)
        : await axiosService.post('/events/', body);
      onSaved?.(data);
      onClose();
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not save the event.'));
    } finally {
      setBusy(false);
    }
  };

  const dtFormat = DATE_FORMAT_MAP[datePref] || 'DD/MM/YYYY HH:mm';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth sx={zIndex ? { zIndex } : undefined}
      slotProps={{ paper: { sx: { borderRadius: 3 } } }}>
      <DialogTitle sx={{ fontWeight: 700 }}>{editing ? 'Edit event' : 'New event'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

          <TextField label="Title *" value={form.title} onChange={set('title')} fullWidth size="small"
            placeholder="e.g. Weekly group meeting, ISMB 2027, Seminar: soil metagenomics" />

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Type</InputLabel>
                <Select value={form.event_type} onChange={set('event_type')} label="Type" {...menu}>
                  {EVENT_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Who can see it</InputLabel>
                <Select value={form.visibility} onChange={set('visibility')} label="Who can see it" {...menu}>
                  {VISIBILITY.map((v) => (
                    <MenuItem key={v.value} value={v.value} disabled={v.value === 'public' && !verified}>{v.label}</MenuItem>
                  ))}
                </Select>
                {!verified && <FormHelperText>Events for everyone need a verified affiliation.</FormHelperText>}
              </FormControl>
            </Grid>
          </Grid>

          {form.visibility === 'lab' && (
            <FormControl fullWidth size="small">
              <InputLabel>Lab *</InputLabel>
              <Select value={form.lab} onChange={set('lab')} label="Lab *" {...menu}>
                {labs.map((l) => <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>)}
              </Select>
              {labs.length === 0 && <FormHelperText>You are not a member of a lab.</FormHelperText>}
            </FormControl>
          )}
          {form.visibility === 'consortium' && (
            <FormControl fullWidth size="small">
              <InputLabel>Consortium *</InputLabel>
              <Select value={form.consortium} onChange={set('consortium')} label="Consortium *" {...menu}>
                {consortia.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
              </Select>
              {consortia.length === 0 && <FormHelperText>You are not a member of a consortium.</FormHelperText>}
            </FormControl>
          )}

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <DateTimePicker label="Starts *" value={form.start_date ? dayjs(form.start_date) : null}
                onChange={(v) => setForm((f) => ({ ...f, start_date: toISO(v) }))} format={dtFormat}
                slotProps={{ textField: { fullWidth: true, size: 'small' }, ...popper }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <DateTimePicker label="Ends" value={form.end_date ? dayjs(form.end_date) : null}
                onChange={(v) => setForm((f) => ({ ...f, end_date: toISO(v) }))} format={dtFormat}
                minDateTime={form.start_date ? dayjs(form.start_date) : undefined}
                slotProps={{ textField: { fullWidth: true, size: 'small' }, ...popper }} />
            </Grid>
          </Grid>

          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 8 }}>
              <TextField label="Place" value={form.location} onChange={set('location')} fullWidth size="small"
                placeholder="Room, building, city or venue" />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControlLabel label="Online"
                control={<Switch checked={form.is_online} onChange={(e) => setForm((f) => ({ ...f, is_online: e.target.checked }))} />} />
            </Grid>
          </Grid>
          {form.is_online && (
            <TextField label="Video-call link" value={form.online_url} onChange={set('online_url')} fullWidth size="small"
              type="url" placeholder="https://…" helperText="Shown only to you and the people who answer or are invited" />
          )}

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Organizer" value={form.organizer} onChange={set('organizer')} fullWidth size="small" />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Website" value={form.website} onChange={set('website')} fullWidth size="small"
                type="url" placeholder="https://…" />
            </Grid>
          </Grid>

          <TextField label="Description" value={form.description} onChange={set('description')}
            fullWidth size="small" multiline minRows={3} />

          {categories.length > 0 && (
            <FormControl fullWidth size="small">
              <InputLabel>Research areas</InputLabel>
              <Select multiple value={form.category_ids} label="Research areas" {...menu}
                onChange={(e) => setForm((f) => ({ ...f, category_ids: e.target.value }))}
                renderValue={(selected) => categories.filter((c) => selected.includes(c.id)).map((c) => c.name).join(', ')}>
                {categories.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
              </Select>
              <FormHelperText>Public events with research areas are suggested in matching proposals</FormHelperText>
            </FormControl>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={busy} sx={{ textTransform: 'none', borderRadius: 2 }}>
          {busy ? 'Saving…' : editing ? 'Save' : 'Create event'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
